import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import { FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";

import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import ConnectionWarningModal from "../ConnectionWarningModal";
import { findWhatsapps } from "../../services/queue.api";

const KeywordGroupForm = ({ group, aiQueues, queueId, onSubmit, onCancel }) => {
  const { user } = useContext(AuthContext);
  const { companyId } = user || {};
  const theme = useTheme();
  
  const [groupName, setGroupName] = useState(group?.groupName || '');
  const [transferQueueId, setTransferQueueId] = useState(group?.transferQueueId || '');
  const [keywords, setKeywords] = useState(group?.keywords?.map(k => k.keyword || k).join(', ') || '');
  const [whatsappId, setWhatsappId] = useState(group?.whatsappId || '');
  const [whatsapps, setWhatsapps] = useState([]);
  const [loadingWhatsapps, setLoadingWhatsapps] = useState(false);
  const [priority, setPriority] = useState(group?.priority || 0);
  const [enabled, setEnabled] = useState(group?.enabled !== undefined ? group.enabled : true);
  const [autoTransfer, setAutoTransfer] = useState(group?.autoTransfer !== undefined ? group.autoTransfer : true);
  const [requireConfirmation, setRequireConfirmation] = useState(group?.requireConfirmation || false);
  const [matchType, setMatchType] = useState(group?.matchType || 'contains');
  const [caseSensitive, setCaseSensitive] = useState(group?.caseSensitive || false);
  const [errors, setErrors] = useState({}); // ✅ NUEVO: Estado para errores de validación
  
  // ✅ NUEVO: Estados para modal de advertencia de conexión desconectada
  const [connectionWarningOpen, setConnectionWarningOpen] = useState(false);
  const [connectionWarningData, setConnectionWarningData] = useState(null);

  // ✅ Cargar conexiones WhatsApp disponibles
  useEffect(() => {
    const loadWhatsapps = async () => {
      if (!companyId) return;
      
      setLoadingWhatsapps(true);
      try {
        const { data } = await findWhatsapps({ companyId, session: 0 });
        setWhatsapps(data || []);
      } catch (err) {
        console.error("Error cargando conexiones WhatsApp:", err);
        toastError(err);
      } finally {
        setLoadingWhatsapps(false);
      }
    };

    loadWhatsapps();
  }, [companyId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // ✅ Limpiar errores previos
    setErrors({});
    
    // ✅ VALIDACIÓN: Nombre del grupo es obligatorio
    if (!groupName || !groupName.trim()) {
      setErrors({ groupName: 'El nombre del grupo es requerido' });
      toast.error('El nombre del grupo es requerido');
      return;
    }
    
    // ✅ VALIDACIÓN: Departamento destino es obligatorio
    if (!transferQueueId || transferQueueId === '') {
      setErrors({ transferQueueId: 'Debes seleccionar un departamento destino' });
      toast.error('Debes seleccionar un departamento destino');
      return;
    }
    
    // ✅ VALIDACIÓN: Palabras clave son obligatorias
    const keywordsArray = keywords.split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);
    
    if (keywordsArray.length === 0) {
      setErrors({ keywords: 'Debes agregar al menos una palabra clave' });
      toast.error('Debes agregar al menos una palabra clave');
      return;
    }

    // ✅ VALIDACIÓN: Verificar estado de conexión WhatsApp antes de guardar (solo si hay whatsappId)
    if (whatsappId && whatsappId !== "" && whatsappId !== 0) {
      const selectedWhatsappObj = whatsapps.find(w => w.id === Number(whatsappId));
      if (selectedWhatsappObj && selectedWhatsappObj.status !== 'CONNECTED') {
        const whatsappName = selectedWhatsappObj.name || `Conexión ${selectedWhatsappObj.id}`;
        
        // ✅ MOSTRAR MODAL DE ADVERTENCIA en lugar de toast rápido
        setConnectionWarningData({
          connectionName: whatsappName,
          connectionStatus: selectedWhatsappObj.status
        });
        setConnectionWarningOpen(true);
        return;
      }
    }

    // ✅ Si todas las validaciones pasan, enviar datos
    onSubmit({
      groupName: groupName.trim(),
      transferQueueId: Number(transferQueueId),
      keywords: keywordsArray,
      whatsappId: whatsappId && whatsappId !== '' ? Number(whatsappId) : null,
      enabled,
      autoTransfer,
      requireConfirmation,
      priority: Number(priority) || 0,
      matchType,
      caseSensitive
    });
  };

  return (
    <>
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: '15px' }}>
        <TextField
          label="Nombre del Grupo"
          value={groupName}
          onChange={(e) => {
            setGroupName(e.target.value);
            // ✅ Limpiar error cuando el usuario escribe
            if (errors.groupName) {
              setErrors(prev => ({ ...prev, groupName: undefined }));
            }
          }}
          fullWidth
          variant="outlined"
          margin="dense"
          required
          error={!!errors.groupName}
          helperText={errors.groupName}
          style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <FormControl 
          fullWidth 
          variant="outlined" 
          margin="dense" 
          required 
          error={!!errors.transferQueueId}
        >
          <InputLabel>Departamento Destino</InputLabel>
          <Select
            value={transferQueueId}
            onChange={(e) => {
              setTransferQueueId(e.target.value);
              // ✅ Limpiar error cuando el usuario selecciona un departamento
              if (errors.transferQueueId) {
                setErrors(prev => ({ ...prev, transferQueueId: undefined }));
              }
            }}
            label="Departamento Destino"
            required
            style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
          >
            <MenuItem value="">Seleccionar...</MenuItem>
            {aiQueues.map((queue) => (
              <MenuItem key={queue.id} value={queue.id}>
                {queue.name}
              </MenuItem>
            ))}
          </Select>
          {errors.transferQueueId && (
            <div style={{ color: '#f44336', fontSize: '0.75rem', marginTop: '4px', marginLeft: '14px' }}>
              {errors.transferQueueId}
            </div>
          )}
        </FormControl>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <TextField
          label="Palabras Clave (separadas por comas)"
          value={keywords}
          onChange={(e) => {
            setKeywords(e.target.value);
            // ✅ Limpiar error cuando el usuario escribe
            if (errors.keywords) {
              setErrors(prev => ({ ...prev, keywords: undefined }));
            }
          }}
          fullWidth
          variant="outlined"
          margin="dense"
          placeholder="comprar, precio, producto, venta"
          helperText={errors.keywords || "Separa las palabras clave con comas"}
          required
          error={!!errors.keywords}
          style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <FormControl 
          fullWidth 
          variant="outlined" 
          margin="dense" 
          error={!!errors.whatsappId}
        >
          <InputLabel>
            <span role="img" aria-label="Conexión WhatsApp">📱</span> Conexión WhatsApp (Opcional)
          </InputLabel>
          <Select
            value={whatsappId}
            onChange={(e) => {
              setWhatsappId(e.target.value);
              // ✅ Limpiar error cuando el usuario selecciona una conexión
              if (errors.whatsappId) {
                setErrors(prev => ({ ...prev, whatsappId: undefined }));
              }
            }}
            label={
              <span>
                <span role="img" aria-label="Conexión WhatsApp">📱</span> Conexión WhatsApp (Opcional)
              </span>
            }
            disabled={loadingWhatsapps}
            style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
            renderValue={() => {
              if (whatsappId === "" || !whatsappId) {
                return (
                  <div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>
                    <span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
                    <span>Usar conexión del ticket (por defecto)</span>
                  </div>
                );
              }
              const whatsapp = whatsapps.find(w => w.id === Number(whatsappId));
              if (!whatsapp) {
                return (
                  <div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>
                    <span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
                    <span>Seleccione una conexión</span>
                  </div>
                );
              }
              return (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                      {whatsapp.name || `Conexión ${whatsapp.id}`}
                    </span>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      {whatsapp.waName ? `${whatsapp.name} - ${whatsapp.waName}` : 
                       whatsapp.name ? whatsapp.name : 
                       `Conexión ${whatsapp.id}`}
                    </span>
                  </div>
                </div>
              );
            }}
          >
            <MenuItem value="">
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <span style={{ marginRight: '8px', fontSize: '16px' }} role="img" aria-label="Por defecto">🔄</span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                  <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                    Usar conexión del ticket (por defecto)
                  </span>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    El sistema usará la conexión WhatsApp del ticket original
                  </span>
                </div>
              </div>
            </MenuItem>
            {whatsapps?.length > 0 &&
              whatsapps.map((whatsapp) => (
                <MenuItem key={whatsapp.id} value={whatsapp.id}>
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <span style={{ marginRight: '8px', fontSize: '16px' }} role="img" aria-label="WhatsApp">📱</span>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                          {whatsapp.name || `Conexión ${whatsapp.id}`}
                        </span>
                        <span 
                          style={{ 
                            fontSize: '10px', 
                            padding: '2px 6px', 
                            borderRadius: '10px',
                            backgroundColor: whatsapp.status === 'CONNECTED' ? '#25d366' : '#e1306c',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        >
                          {whatsapp.status}
                        </span>
                      </div>
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {whatsapp.waName ? `${whatsapp.name} - ${whatsapp.waName}` : 
                         whatsapp.name ? whatsapp.name : 
                         `Conexión ${whatsapp.id}`}
                      </span>
                    </div>
                  </div>
                </MenuItem>
              ))}
          </Select>
          {errors.whatsappId && (
            <div style={{ color: '#f44336', fontSize: '0.75rem', marginTop: '4px', marginLeft: '14px' }}>
              {errors.whatsappId}
            </div>
          )}
        </FormControl>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
        <TextField
          label="Prioridad"
          type="number"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          variant="outlined"
          margin="dense"
          helperText="Mayor número = mayor prioridad"
          style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
        />
        
        <FormControl variant="outlined" margin="dense">
          <InputLabel>Tipo de Coincidencia</InputLabel>
          <Select
            value={matchType}
            onChange={(e) => setMatchType(e.target.value)}
            label="Tipo de Coincidencia"
            style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
          >
            <MenuItem value="contains">Contiene</MenuItem>
            <MenuItem value="exact">Exacta</MenuItem>
            <MenuItem value="partial">Parcial</MenuItem>
          </Select>
        </FormControl>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', color: theme.mode === 'dark' ? theme.palette.text.primary : '#333' }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          <span>Grupo activado</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', color: theme.mode === 'dark' ? theme.palette.text.primary : '#333' }}>
          <input
            type="checkbox"
            checked={autoTransfer}
            onChange={(e) => setAutoTransfer(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          <span>Transferir inmediatamente al detectar</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', color: theme.mode === 'dark' ? theme.palette.text.primary : '#333' }}>
          <input
            type="checkbox"
            checked={requireConfirmation}
            onChange={(e) => setRequireConfirmation(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          <span>Requerir confirmación del usuario</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', color: theme.mode === 'dark' ? theme.palette.text.primary : '#333' }}>
          <input
            type="checkbox"
            checked={caseSensitive}
            onChange={(e) => setCaseSensitive(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          <span>Sensible a mayúsculas/minúsculas</span>
        </label>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <Button
          variant="outlined"
          onClick={onCancel}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
        >
          {group ? 'Actualizar' : 'Crear'} Grupo
        </Button>
      </div>
    </form>

    {/* ✅ NUEVO: Modal de advertencia de conexión desconectada */}
    <ConnectionWarningModal
      open={connectionWarningOpen}
      onClose={() => {
        setConnectionWarningOpen(false);
        setConnectionWarningData(null);
      }}
      connectionName={connectionWarningData?.connectionName || ""}
      connectionStatus={connectionWarningData?.connectionStatus || ""}
    />
  </>
  );
};

export default KeywordGroupForm;
